import type { MarketplaceId } from "@nova-ai/shared"

import { useMarketplaces } from "../services/use-dashboard"
import { useRuns } from "../services/use-runs"

type RunsFilterOptions = {
  marketplaces: readonly MarketplaceId[]
  startedBy: readonly string[]
}

/**
 * Sources the option lists for the runs filters.
 *
 * Marketplaces come from the canonical /api/marketplaces list. "Started by"
 * has no dedicated endpoint, so we derive the distinct actors from every run in
 * the current view (active or inclusive) — deliberately NOT the user-filtered
 * list, so picking a person never collapses the dropdown to that one person.
 * When no other filter is active this query shares a key with the table's own
 * runs query, so React Query dedupes it into a single request.
 */
export function useRunsFilterOptions(includeArchived: boolean): RunsFilterOptions {
  const marketplaces = useMarketplaces()
  const viewRuns = useRuns({ archived: includeArchived ? "all" : false })

  const seen = new Set<string>()
  for (const run of viewRuns.data?.runs ?? []) {
    if (run.startedBy) {
      seen.add(run.startedBy)
    }
  }
  const startedBy = [...seen].sort((a, b) => a.localeCompare(b))

  return { marketplaces: marketplaces.data ?? [], startedBy }
}
